/**
 * GoogleSheetsExportConfig Component
 *
 * Configuration component for Google Sheets auto-export.
 * Allows users to enable export and choose mode (create / create & override).
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 * @param {Object} props.taskConfig - Full task configuration (for accessing task name)
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cloudApi from "../../../../../services/cloudApi";

const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const MODES = [
  { value: "create", label: "Create new spreadsheet", description: "A new spreadsheet is created for each run" },
  { value: "create_override", label: "Create new spreadsheet & Override", description: "Creates a spreadsheet on first run, then overwrites it on subsequent runs" },
];

const GoogleSheetsExportConfig = ({ config, onChange, taskConfig }) => {
  const navigate = useNavigate();
  const [integrationStatus, setIntegrationStatus] = useState(null);

  const taskName = taskConfig?.general?.name || taskConfig?.metadata?.title || "";

  // Pre-fill spreadsheetTitle with task name + date when not yet set
  useEffect(() => {
    if (taskName && !config.spreadsheetTitle) {
      const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      onChange({ ...config, spreadsheetTitle: `${taskName} - ${dateStr}` });
    }
  }, [taskName]);

  // Check if Google Sheets is connected at workspace level
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const integrations = await cloudApi.getIntegrations();
        const gs = integrations.find((i) => i.provider === "google_sheets");
        setIntegrationStatus(gs?.status || "not_connected");
      } catch {
        setIntegrationStatus(null);
      }
    };
    checkConnection();
  }, []);

  const handleClearSpreadsheet = () => {
    onChange({
      ...config,
      spreadsheetId: null,
      spreadsheetName: null,
      spreadsheetUrl: null,
    });
  };

  const handleModeChange = (mode) => {
    const updates = { ...config, mode };
    // Clear spreadsheet selection when switching to create mode
    if (mode === "create") {
      updates.spreadsheetId = null;
      updates.spreadsheetName = null;
      updates.spreadsheetUrl = null;
    }
    onChange(updates);
  };

  const isConnected = integrationStatus === "active";

  return (
    <div className="space-y-2.5">
      {/* Connection status banner */}
      {!isConnected && integrationStatus !== null && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20">
          <span className="text-amber-500 text-sm">!</span>
          <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">
            Google Sheets is not connected.{" "}
            <button
              onClick={() => navigate("/dashboard/integrations")}
              className="underline font-medium hover:text-amber-800 dark:hover:text-amber-200"
            >
              Connect now
            </button>
          </p>
        </div>
      )}

      {/* Enable toggle */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Google Sheets Export</label>
            <p className="text-2xs text-gray-400 dark:text-gray-500">Automatically export data after each run</p>
          </div>
          <Toggle
            enabled={config.enabled}
            onChange={(enabled) => onChange({ ...config, enabled })}
            disabled={!isConnected}
          />
        </div>
      </div>

      {config.enabled && isConnected && (
        <>
          {/* Mode selector */}
          <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide mb-3">Export Mode</h3>
            <div className="space-y-2">
              {MODES.map((mode) => (
                <label
                  key={mode.value}
                  className={`flex items-start gap-3 p-2.5 rounded-xl cursor-pointer transition-colors ${
                    config.mode === mode.value
                      ? "bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20"
                      : "hover:bg-gray-50 dark:hover:bg-white/[0.02] border border-transparent"
                  }`}
                >
                  <input
                    type="radio"
                    name="sheetsExportMode"
                    value={mode.value}
                    checked={config.mode === mode.value}
                    onChange={() => handleModeChange(mode.value)}
                    className="mt-0.5 accent-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{mode.label}</span>
                    <p className="text-2xs text-gray-400 dark:text-gray-500">{mode.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Linked spreadsheet display (for create_override when a spreadsheet was already created) */}
          {config.mode === "create_override" && config.spreadsheetId && (
            <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
              <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide mb-3">
                Linked Spreadsheet
              </h3>
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {config.spreadsheetName || "Linked spreadsheet"}
                  </p>
                  {config.spreadsheetUrl && (
                    <a
                      href={config.spreadsheetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xs text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      Open in Google Sheets
                    </a>
                  )}
                </div>
                <button
                  onClick={handleClearSpreadsheet}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  title="Reset - a new spreadsheet will be created on next run"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1.5">
                Data will be overwritten in this spreadsheet on each run. Clear to create a new one.
              </p>
            </div>
          )}

          {/* Spreadsheet name */}
          <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide mb-3">Spreadsheet Name</h3>
            <input
              type="text"
              value={config.spreadsheetTitle || ""}
              onChange={(e) => onChange({ ...config, spreadsheetTitle: e.target.value })}
              placeholder={taskName || "Export"}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700/50 rounded-xl
                         bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-200
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 outline-none"
            />
            <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1.5">
              The spreadsheet name in Google Drive.
            </p>
          </div>

          {/* Hint about finding sheets */}
          <p className="text-2xs text-gray-400 dark:text-gray-500 px-1">
            View all your spreadsheets in{" "}
            <a href="https://docs.google.com/spreadsheets/" target="_blank" rel="noopener noreferrer"
               className="text-emerald-600 dark:text-emerald-400 hover:underline">
              Google Sheets
            </a>
            . Links to exported spreadsheets are also available in each run's details.
          </p>
        </>
      )}
    </div>
  );
};

export default GoogleSheetsExportConfig;
